/**
 * AppHeader — 상단 글로벌 헤더 (로고/브레드크럼/locale/테마/알림/사용자 메뉴).
 *
 * 【역할】
 * - 애플리케이션 좌측 로고 + 현재 경로 브레드크럼 표시.
 * - 우측 컨트롤: locale 전환(Dropdown), 다크/라이트 토글, 설정 패널(ColorPicker/FontSize/Layout),
 *   알림 Badge(unread count 폴링 30초), 사용자 메뉴(프로필/로그아웃).
 *
 * 【주요 기능】
 * - useLocale.setLocale(loc) — 7단계 파이프라인 트리거 (useLocale.ts 참조).
 * - 30초 간격 notification/searchList 폴링 → 미읽음 알림 badge count.
 * - settingsPanel — ColorPicker + PRESET_COLORS 6종 + light/dark/compact/fontSize 슬라이더 + layout side/top.
 *
 * 【사용 위치】
 * - RootLayout 내부 `<Header>` 슬롯. 로그인 후 모든 페이지 상단에 고정 표시.
 *
 * 【포팅 원본】 react-spring-fw AppHeader.vue (동일 구조, Composition API → Hooks 전환).
 *
 * 【의존성】
 * - useAuthStore, useLocale, useSupportedLocales, useLabel, useTheme, useThemeStore
 * - useDataSet, useTransaction (알림 폴링)
 */
import { useEffect, useState } from 'react'
import {
  Breadcrumb,
  Dropdown,
  Avatar,
  Badge,
  Space,
  Flex,
  Button,
  MenuProps,
  theme as antdTheme,
  Radio,
  ColorPicker,
  Divider,
  Typography,
  Switch,
  Slider
} from 'antd'
import {
  BellOutlined,
  UserOutlined,
  LogoutOutlined,
  GlobalOutlined,
  BulbOutlined,
  BulbFilled,
  SettingOutlined
} from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { useLocale, useSupportedLocales } from '@/framework/useLocale'
import { useLabel } from '@/framework/useLabel'
import { useTheme } from '@/framework/useTheme'
import { useThemeStore } from '@/store/themeStore'
import { useDataSet, useTransaction } from '@/framework'
import { useFrameworkContext } from '@/framework/FrameworkProvider'
import { useAlertSocket, AlertMessage } from '@/framework/useAlertSocket'
import { SKIN_ORDER } from '@/design-skins'
import { SkinSwatch } from '@/components/common/SkinSwatch'

/**
 * AppHeader — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function AppHeader() {
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const navigate = useNavigate()
  const location = useLocation()
  const { locale, setLocale } = useLocale()
  const supportedLocales = useSupportedLocales()
  const { t } = useLabel()
  const {
    mode,
    toggleDark,
    primaryColor,
    setPrimaryColor,
    setMode,
    compact,
    setCompact,
    fontSize,
    setFontSize,
    preset,
    applyPreset,
    presets,
    skin,
    setSkin,
    skins
  } = useTheme()

  // ─── 6종 프리셋 색상 (settingsPanel ColorPicker 우측 swatch 칩) ─
  // - 키는 i18n 라벨용 (`LBL_COLOR_RED` 등 자동 매핑은 안 됨 — 시각적 식별용)
  // - 색상 값은 antd 기본 팔레트 (volcano/orange/green/cyan/blue/purple)
  const PRESET_COLORS = [
    { key: 'red', color: '#f5222d' },
    { key: 'orange', color: '#fa8c16' },
    { key: 'green', color: '#52c41a' },
    { key: 'cyan', color: '#13c2c2' },
    { key: 'blue', color: '#1677ff' },
    { key: 'purple', color: '#722ed1' }
  ]
  const layoutMode = useThemeStore((s) => s.layout)
  const setLayout = useThemeStore((s) => s.setLayout)
  const { token } = antdTheme.useToken()
  const notifDs = useDataSet<{ isRead?: boolean | string | number }>()
  const { transaction } = useTransaction()
  const [unreadCount, setUnreadCount] = useState(0)
  const authToken = useAuthStore((s) => s.token)

  // 알림 미읽음 카운트 폴링 (30초 간격, 인증된 세션 한정)
  //  - loading=false 로 전역 Spin 억제 (헤더 백그라운드 호출이라 사용자 불편 회피)
  //  - cancelled 플래그로 unmount 후 setState 차단
  useEffect(() => {
    if (!authToken) return
    let cancelled = false
    const fetchNotif = async () => {
      try {
        const res = await transaction({
          id: 'notif',
          service: 'notification/searchList',
          action: 'search',
          outds: { ds_notification: notifDs },
          loading: false // 전역 Spin 억제 (헤더 백그라운드 폴링)
        })
        if (!res.success || cancelled) return
        const count = notifDs.getVisibleRows().filter((r) => {
          const f = r.data.isRead
          return !(f === true || f === 'Y' || f === 1 || f === '1' || f === 'true')
        }).length
        setUnreadCount(count)
      } catch {
        /* ignore */
      }
    }
    fetchNotif()
    const t = setInterval(fetchNotif, 30000)
    return () => {
      cancelled = true
      clearInterval(t)
    }
  }, [authToken])

  // WS 실시간 알림 — badge 즉시 +1, 토스트, 페이지 갱신 이벤트('pharm:alert') 전파.
  // 30초 폴링은 유지 (WS 단절 구간 보정 + 정확한 미읽음 카운트 동기화)
  const { notification: notify } = useFrameworkContext()
  useAlertSocket((msg: AlertMessage) => {
    setUnreadCount((c) => c + 1)
    const open = msg.severity === 'CRITICAL' ? notify.error : notify.warning
    open({ message: msg.title, description: msg.body, placement: 'topRight', duration: 6 })
    window.dispatchEvent(new CustomEvent('pharm:alert', { detail: msg }))
  })

  /** 로그아웃: authStore.logout (토큰 클린업) → /login 강제 이동 (replace) */
  const onLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  // ─── 사용자 Avatar 클릭 드롭다운 메뉴 (프로필/로그아웃) ─
  const userMenu: MenuProps['items'] = [
    { key: 'profile', label: t('MENU_PROFILE', '프로필'), icon: <UserOutlined /> },
    { type: 'divider' },
    {
      key: 'logout',
      label: t('BTN_LOGOUT', '로그아웃'),
      icon: <LogoutOutlined />,
      onClick: onLogout
    }
  ]

  // ─── locale 전환 드롭다운 (지원 locale × {flag + nativeName}) ─
  // 클릭 시 useLocale.setLocale → 7단계 i18n 파이프라인 트리거 (label/code/menu 캐시 무효화)
  const localeMenu: MenuProps['items'] = supportedLocales.map((l) => ({
    key: l.locale,
    label: `${l.flagEmoji ?? ''} ${l.nativeName ?? l.name ?? l.locale.toUpperCase()}`,
    onClick: () => void setLocale(l.locale)
  }))

  // 브레드크럼 빵부스러기 — 경로 분할 ('/admin/user' → ['admin', 'user'])
  const crumbs = location.pathname.split('/').filter(Boolean)

  const settingsPanel = (
    <div
      style={{
        padding: token.padding,
        width: 320,
        maxHeight: '80vh',
        overflowY: 'auto',
        background: token.colorBgElevated,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadowSecondary
      }}
    >
      {/* ─── 섹션 1. 디자인 스킨 (SKIN_ORDER 의 5종 swatch) ─ */}
      {/* 클릭 시 useThemeStore.setSkin → tokens 일괄 교체 */}
      <Flex vertical gap={token.marginXS}>
        <Typography.Text strong>{t('LBL_SKIN', '디자인 스킨')}</Typography.Text>
        <Flex gap={token.paddingXS} wrap>
          {SKIN_ORDER.map((key) => (
            <SkinSwatch
              key={key}
              def={skins[key]}
              active={skin === key}
              onClick={() => setSkin(key)}
            />
          ))}
        </Flex>
      </Flex>
      <Divider style={{ marginBlock: token.marginSM }} />
      {/* ─── 섹션 2. 테마 프리셋 (스킨 내부 컬러 변형) ─ */}
      {/* presets[key] = { primaryColor, label } 매핑 — 클릭 시 applyPreset */}
      <Flex vertical gap={token.marginXS}>
        <Typography.Text strong>{t('LBL_PRESET', '테마 프리셋')}</Typography.Text>
        <Flex gap={token.paddingXS} wrap>
          {Object.values(presets).map((p) => {
            const active = preset === p.key
            return (
              <div
                key={p.key}
                role="button"
                aria-label={`preset-${p.key}`}
                onClick={() => applyPreset(p.key)}
                title={p.label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  borderRadius: token.borderRadius,
                  cursor: 'pointer',
                  border: `1px solid ${active ? p.primaryColor : token.colorBorderSecondary}`,
                  background: active ? `${p.primaryColor}1a` : 'transparent',
                  transition: 'all 0.15s'
                }}
              >
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: p.primaryColor
                  }}
                />
                <Typography.Text style={{ fontSize: token.fontSizeSM }}>{p.label}</Typography.Text>
              </div>
            )
          })}
        </Flex>
      </Flex>
      <Divider style={{ marginBlock: token.marginSM }} />
      {/* ─── 섹션 3. 레이아웃 모드 (사이드 vs 상단) ─ */}
      {/* RootLayout 이 layoutMode 구독해 Sider/Header 배치 전환 */}
      <Flex vertical gap={token.marginXS}>
        <Typography.Text strong>{t('LBL_LAYOUT', '레이아웃')}</Typography.Text>
        <Radio.Group
          value={layoutMode}
          onChange={(e) => setLayout(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="side">{t('LBL_LAYOUT_SIDE', '사이드')}</Radio.Button>
          <Radio.Button value="top">{t('LBL_LAYOUT_TOP', '상단')}</Radio.Button>
        </Radio.Group>
      </Flex>
      <Divider style={{ marginBlock: token.marginSM }} />
      {/* ─── 섹션 4. 테마 모드 (라이트 vs 다크) ─ */}
      {/* mode 변경 → antd ConfigProvider algorithm 자동 전환 */}
      <Flex vertical gap={token.marginXS}>
        <Typography.Text strong>{t('LBL_THEME', '테마')}</Typography.Text>
        <Radio.Group
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="light">{t('LBL_THEME_LIGHT', '라이트')}</Radio.Button>
          <Radio.Button value="dark">{t('LBL_THEME_DARK', '다크')}</Radio.Button>
        </Radio.Group>
      </Flex>
      <Divider style={{ marginBlock: token.marginSM }} />
      {/* ─── 섹션 5. 주 색상 (PRESET_COLORS 6종 + ColorPicker) ─ */}
      {/* primaryColor 변경 → tokens.colorPrimary + 파생 색상 일괄 재계산 */}
      <Flex vertical gap={token.marginXS}>
        <Typography.Text strong>{t('LBL_PRIMARY_COLOR', '주 색상')}</Typography.Text>
        <Flex gap={token.paddingXS} wrap>
          {PRESET_COLORS.map((p) => {
            const active = primaryColor.toLowerCase() === p.color.toLowerCase()
            return (
              <div
                key={p.key}
                role="button"
                aria-label={`preset-${p.key}`}
                onClick={() => setPrimaryColor(p.color)}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: p.color,
                  cursor: 'pointer',
                  border: active ? `2px solid ${token.colorText}` : '2px solid transparent',
                  boxShadow: active ? `0 0 0 2px ${p.color}33` : undefined,
                  transition: 'all 0.15s'
                }}
              />
            )
          })}
        </Flex>
        <ColorPicker
          value={primaryColor}
          onChange={(c) => setPrimaryColor(c.toHexString())}
          showText
        />
      </Flex>
      <Divider style={{ marginBlock: token.marginSM }} />
      <Flex justify="space-between" align="center">
        <Typography.Text strong>{t('LBL_COMPACT', 'Compact 모드')}</Typography.Text>
        <Switch checked={compact} onChange={setCompact} />
      </Flex>
      <Divider style={{ marginBlock: token.marginSM }} />
      <Flex vertical gap={token.marginXXS}>
        <Flex justify="space-between" align="center">
          <Typography.Text strong>{t('LBL_FONT_SIZE', '폰트 크기')}</Typography.Text>
          <Typography.Text type="secondary">{fontSize}px</Typography.Text>
        </Flex>
        <Slider
          min={12}
          max={18}
          step={1}
          value={fontSize}
          onChange={(v) => setFontSize(v as number)}
        />
      </Flex>
    </div>
  )

  return (
    <Flex
      align="center"
      justify="space-between"
      style={{
        paddingInline: token.padding,
        height: 64,
        borderBottom: `1px solid ${token.colorBorderSecondary}`
      }}
    >
      <Flex align="center" gap={token.paddingLG}>
        <div style={{ fontWeight: 700, fontSize: token.fontSizeHeading5 }}>
          {t('APP_TITLE', 'Monitoring Center')}
        </div>
        <Breadcrumb
          items={[{ title: t('BREADCRUMB_HOME', 'Home') }, ...crumbs.map((c) => ({ title: c }))]}
        />
      </Flex>
      <Space size="middle">
        <Dropdown menu={{ items: localeMenu }} placement="bottomRight">
          <Button type="text" icon={<GlobalOutlined />}>
            {locale.toUpperCase()}
          </Button>
        </Dropdown>
        <Button
          type="text"
          icon={mode === 'dark' ? <BulbFilled /> : <BulbOutlined />}
          onClick={toggleDark}
        />
        <Dropdown trigger={['click']} placement="bottomRight" popupRender={() => settingsPanel}>
          <Button type="text" icon={<SettingOutlined />} />
        </Dropdown>
        <Badge
          count={unreadCount > 0 ? unreadCount : 0}
          showZero={false}
          offset={[-2, 2]}
          style={{ zIndex: 1500 }}
        >
          <Button
            type="text"
            icon={<BellOutlined />}
            onClick={() => navigate('/templates/notification')}
          />
        </Badge>
        <Dropdown menu={{ items: userMenu }} placement="bottomRight">
          <Space style={{ cursor: 'pointer' }}>
            <Avatar icon={<UserOutlined />} />
            <span>{user?.userName ?? 'Guest'}</span>
          </Space>
        </Dropdown>
      </Space>
    </Flex>
  )
}
